import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { TenantTicket, TicketStatusEnum } from '../../../apis/types'
import { formatDistanceToNow } from 'date-fns'

interface TicketItemProps {
  ticket: TenantTicket
}

const statusColors: Record<TicketStatusEnum, { bg: string; text: string }> = {
  OPEN: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200' },
  IN_PROGRESS: { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200' },
  DONE: { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200' },
  CLOSED: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-200' },
}

const TicketItem: React.FC<TicketItemProps> = ({ ticket }) => {
  const statusColor = statusColors[ticket.status] || statusColors.OPEN
  const updatedAgo = ticket.externalUpdatedAt
    ? formatDistanceToNow(new Date(ticket.externalUpdatedAt), { addSuffix: true })
    : null

  return (
    <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <a
              href={ticket.ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-1"
            >
              {ticket.ticketKey}
              <ArrowTopRightOnSquareIcon className="w-3 h-3" />
            </a>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor.bg} ${statusColor.text}`}>
              {ticket.externalStatus || ticket.status.replace('_', ' ')}
            </span>
            {ticket.priority && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
                {ticket.priority}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 line-clamp-2">{ticket.summary}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            {ticket.tenantAccountName && (
              <span className="bg-slate-100 dark:bg-slate-600 px-2 py-0.5 rounded">
                {ticket.tenantAccountName}
              </span>
            )}
            {ticket.matchedAccountNumber && (
              <span className="bg-slate-100 dark:bg-slate-600 px-2 py-0.5 rounded">
                #{ticket.matchedAccountNumber}
              </span>
            )}
            {updatedAgo && <span>Updated {updatedAgo}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TicketItem
